/**
 * Split document text around the query terms the engine reported as matched
 * (OMA-639), so the result-detail dialog can mark them.
 *
 * The backend sends chunk text as PLAIN text on purpose — no `<mark>`, so the
 * panel needs no sanitiser — which means the highlight has to be produced here
 * and rendered as elements, never as HTML. The segments always rejoin into the
 * original string, so nothing the document said can be dropped or altered by
 * highlighting it.
 *
 * Pure and framework-free: pinned by `tests/unit/search-highlight-terms.test.ts`.
 */

export interface HighlightSegment {
  text: string;
  /** True when this segment is one of the matched query terms. */
  match: boolean;
}

/** Escape the characters that would otherwise be regex syntax. */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function highlightSegments(
  text: string,
  tokens: readonly string[]
): HighlightSegment[] {
  if (!text) {
    return [];
  }

  // A single character matches almost everywhere in Arabic text and would turn
  // the passage into confetti; the engine's own tokens are words.
  const usable = Array.from(
    new Set(tokens.map((token) => token.trim()).filter((t) => t.length > 1))
  )
    // Longest first, so "الهوية الوطنية" wins over "الهوية" at the same offset
    // instead of being cut short by it.
    .sort((a, b) => b.length - a.length);

  if (usable.length === 0) {
    return [{ text, match: false }];
  }

  const pattern = new RegExp(usable.map(escapeRegExp).join('|'), 'gi');
  const segments: HighlightSegment[] = [];
  let cursor = 0;

  for (const found of text.matchAll(pattern)) {
    const start = found.index ?? 0;
    if (start > cursor) {
      segments.push({ text: text.slice(cursor, start), match: false });
    }
    segments.push({ text: found[0], match: true });
    cursor = start + found[0].length;
  }

  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), match: false });
  }

  return segments;
}
